import type { Entry } from '../../../features/admin/modules/modules.model';

/**
 * The persisted entry a field should read from and write to, resolved from the formly form state.
 *
 * `formState.entry` is normally the persisted entry merged with the live form value. On the
 * query-param path (`/onboarding?userId=…`, admin hand-over) the hosting page resolves its entry
 * through EntriesService instead of ModulesStore, so DetailsService has nothing to merge and
 * `formState.entry` degrades to a shell holding form values only — no `id`, no `moduleId`, none of
 * the entry-level keys (`status`, `user`, `company`). The page publishes the real record as
 * `formState.hostEntry` for exactly that case.
 *
 * The identity is the discriminator, checked in that order: `entry` wins whenever it has an id (it
 * is the fresher of the two, carrying the live model); otherwise the host entry wins if IT has an
 * id. Opus-verified 2026-09-14: an id-less `hostEntry` must NOT be preferred over an
 * id-less `entry` — a caller that reads `.id`/`.moduleId`/`._etag` off the result to build a PATCH
 * envelope would then read those off the wrong (still id-less) object instead of falling through to
 * whichever side actually has form values. `entry` is still returned as the final fallback (over
 * `hostEntry`) so a caller that only needs form values keeps the live one it had, matching what
 * this returned before `hostEntry` existed.
 *
 * Deliberately a plain function called fresh at every use, NOT something to memoize on:
 * `options.formState` is a mutable plain object with zero signal producers, so a `computed()`
 * around it would evaluate once and never invalidate — not even when the host reassigns
 * `options.formState` wholesale.
 */
export function anchorEntryOf(formState: unknown): Entry | null {
  const state = formState as Record<string, unknown> | undefined;
  const live = state?.['entry'] as Entry | undefined;
  const host = state?.['hostEntry'] as Entry | undefined;
  if (live?.id) return live;
  if (host?.id) return host;
  return live ?? host ?? null;
}
